import type { AdminAbout, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { BilingualField, OptionalBilingualField } from '@/components/bilingual';
import { ImagePicker } from '@/components/image-picker';
import { PublishBlockers, StatusBadge } from '@/components/records';
import { Alert, Button, Card, SkeletonRows } from '@/components/ui';
import { apiGet, apiPatch, apiPost, useAdminMutation } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';

const URL = '/admin/content/about';
const BODY_MAX = 20_000;

export function AboutPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const query = useQuery({ queryKey: qk.about, queryFn: () => apiGet<AdminAbout>(URL) });
  const about = query.data?.data;

  const [title, setTitle] = useState<LocalizedPair>({ ar: '', en: '' });
  const [introduction, setIntroduction] = useState<LocalizedPair>({ ar: '', en: '' });
  const [body, setBody] = useState<LocalizedPair>({ ar: '', en: '' });
  const [seoTitle, setSeoTitle] = useState<LocalizedPair>({ ar: '', en: '' });
  const [seoDescription, setSeoDescription] = useState<LocalizedPair>({ ar: '', en: '' });
  const [imageId, setImageId] = useState<string | null>(null);
  const [image, setImage] = useState<{ id: string; width: number; height: number; thumbUrl: string; url: string } | null>(null);

  useEffect(() => {
    if (!about) return;
    setTitle(about.title);
    setIntroduction(editablePair(about.introduction));
    setBody(editablePair(about.body));
    setSeoTitle(editablePair(about.seo.title));
    setSeoDescription(editablePair(about.seo.description));
    setImageId(about.imageId);
  }, [about]);

  const invalidate = [qk.about, qk.readiness];

  const save = useAdminMutation(
    () =>
      apiPatch(URL, {
        expectedVersion: about?.version ?? 1,
        titleAr: title.ar,
        titleEn: title.en,
        introductionAr: introduction.ar,
        introductionEn: introduction.en,
        bodyAr: body.ar,
        bodyEn: body.en,
        seoTitleAr: seoTitle.ar,
        seoTitleEn: seoTitle.en,
        seoDescAr: seoDescription.ar,
        seoDescEn: seoDescription.en,
        imageId,
      }),
    { successMessage: t('common.saved'), invalidate },
  );

  const publish = useAdminMutation(
    () => apiPost(`${URL}/publish`, { expectedVersion: about?.version ?? 1 }),
    { successMessage: t('publish.published'), invalidate },
  );

  const unpublish = useAdminMutation(
    () => apiPost(`${URL}/unpublish`, { expectedVersion: about?.version ?? 1 }),
    { successMessage: t('publish.unpublished'), invalidate },
  );

  if (query.isLoading || !about) return <SkeletonRows rows={6} />;

  return (
    <>
      <PageHeader
        width="narrow"
        title={
          <span className="flex items-center gap-2">
            {t('about.title')}
            <StatusBadge status={about.publicationStatus} />
          </span>
        }
        actions={
          canEdit && (
            <div className="flex gap-2">
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
                {t('common.save')}
              </Button>
              {about.publicationStatus === 'PUBLISHED' ? (
                <Button loading={unpublish.isPending} onClick={() => unpublish.mutate(undefined)}>
                  {t('publish.unpublish')}
                </Button>
              ) : (
                <Button
                  loading={publish.isPending}
                  disabled={!about.translation.publishable}
                  onClick={() => publish.mutate(undefined)}
                >
                  {t('publish.publish')}
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="flex flex-col gap-4">
            <PublishBlockers translation={about.translation} />

            <BilingualField
              label={t('about.pageTitle')}
              value={title}
              onChange={setTitle}
              required
              maxLength={140}
              disabled={!canEdit}
            />

            <OptionalBilingualField
              label={t('about.introduction')}
              value={introduction}
              onChange={setIntroduction}
              multiline
              rows={3}
              maxLength={500}
              disabled={!canEdit}
            />

            <BilingualField
              label={t('about.body')}
              hint={t('about.bodyHint')}
              value={body}
              onChange={setBody}
              required
              multiline
              rows={14}
              maxLength={BODY_MAX}
              disabled={!canEdit}
            />

            <ImagePicker
              label={t('about.image')}
              surface="offer"
              value={image ? { ...image, storageKey: '', alt: { ar: '', en: '' } } : null}
              disabled={!canEdit}
              onChange={(picked) => {
                setImageId(picked?.id ?? null);
                setImage(picked);
              }}
            />
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card title={t('about.seoTitle')}>
            <div className="flex flex-col gap-4">
              <OptionalBilingualField
                label={t('about.seoTitle')}
                value={seoTitle}
                onChange={setSeoTitle}
                maxLength={70}
                disabled={!canEdit}
              />
              <OptionalBilingualField
                label={t('about.seoDescription')}
                value={seoDescription}
                onChange={setSeoDescription}
                multiline
                rows={3}
                maxLength={160}
                disabled={!canEdit}
              />
            </div>
          </Card>

          <Card title={t('common.preview')}>
            <Alert tone="info">{t('about.bodyHint')}</Alert>
            {/*
              This renders the SAVED body, not what is in the textarea.

              The stored value has already been through the server's allowlist —
              rendering the unsaved draft instead would be putting raw typed
              markup into the DOM, which is the one thing the write-time
              sanitiser exists to make unnecessary.
            */}
            <div
              className="mt-3"
              lang={locale}
              dir={locale === 'ar' ? 'rtl' : 'ltr'}
              dangerouslySetInnerHTML={{ __html: about.body?.[locale] ?? '' }}
            />
          </Card>
        </div>
      </div>
    </>
  );
}
